import { useCallback } from 'react';
import type { Message, MessageV2 } from '@/hooks/useCommunication';
import { useCommunication } from '@/hooks/useCommunication';
import type { ISerialPort } from '@/hooks/useSerialPort';
import { collectLines, readReader, useSerialPort } from '@/hooks/useSerialPort';
import { BwsLlNbEncoder } from '@/lib/protocol/encoder/bws-ll-nb';
import type { BwsLlNb } from '@/lib/protocol/parser/bws-ll-nb';
import { BwsLlNbParser } from '@/lib/protocol/parser/bws-ll-nb';
import { isIccid, isImei, sleep, typedObjectEntries } from '@/lib/utils';
import { lorawanTimestamp, verifyLorawanKeys } from '@/lib/utils/generate-key-lora-wan';
import type {
  BwsLlNbConfig,
  IConfigurationProfile,
} from '@/types/production/configuration-profile';
import type { Device } from '@/types/production/device';

namespace Namespace {
  export interface Equipment {
    firmware: string;
    serial: string;
    imei?: string | undefined;
    iccid?: string | undefined;
    lora_keys?: Partial<Device['Equipment']['lora_keys']>;
  }
  export interface Detected {
    port: ISerialPort;
    equipment: Equipment;
  }

  export type Profile = IConfigurationProfile<BwsLlNbConfig>['config'];

  export type ConfigKeys =
    | keyof NonNullable<Profile['general']>
    | keyof NonNullable<Profile['specific']>;
}

/**
 * BWS LL NB, BWS LL NB+LoRa, BWS BAIT NB e BWS BAIT NB+LoRa são dispositivos diferentes que
 * falam o mesmo protocolo serial (manual BWS LL NB+LoRa v1.0.0); o modelo muda o
 * `technology_system_name` do perfil, o DEV esperado no autoteste e a presença do rádio LoRa.
 */
type BwsLlNbModel = 'DM_BWS_LL_NB' | 'DM_BWS_LL_NB_LORA' | 'DM_BWS_BAIT_NB' | 'DM_BWS_BAIT_NB_LORA';

// Só os modelos "+LoRa" têm rádio LoRa: os demais não gravam nem conferem serial,
// timestamp e chaves LoRa, e o autoteste não cobra o RF.
const hasLoraRadio = (model: BwsLlNbModel) =>
  model === 'DM_BWS_LL_NB_LORA' || model === 'DM_BWS_BAIT_NB_LORA';

// Time out de SOS (min) gravado com WFST ao final da identificação.
const IDENTIFICATION_SOS_TIMEOUT_MIN = 10;

const readResponse = async (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  command: string,
  timeout: number = 500,
  check?: string,
): Promise<string | undefined> => {
  const decoder = new TextDecoder();
  let buffer = '';

  const base = command.replace('\r\n', '');
  const cmp = check ? check : base.includes('=') ? base : `${base}=`;

  const timeoutPromise = new Promise<undefined>((resolve) =>
    setTimeout(() => resolve(undefined), timeout),
  );

  const readPromise = (async () => {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value);

      console.info('[RAW DATA]', buffer);

      const lines = buffer.split('\r\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.length > 0 && line.includes(cmp)) {
          return line;
        }
      }
    }
    return undefined;
  })();

  return Promise.race([readPromise, timeoutPromise]);
};

// Casa cada comando à linha de resposta correspondente: procura o prefixo do comando,
// com '=' quando o comando não o contém (ex.: "RCM\r\n" → "RCM=").
const matchResponse = (lines: string[], command: string): string | undefined => {
  const base = command.replace(/\r\n?/g, '');
  const cmp = base.includes('=') ? base : `${base}=`;
  return lines.find((line) => line.length > 0 && line.includes(cmp));
};

const generateMessages = (profile: IConfigurationProfile): Record<Namespace.ConfigKeys, string> => {
  const response = {} as Record<Namespace.ConfigKeys, string>;
  Object.entries({
    ...profile.config.general,
    ...profile.config.specific,
  }).forEach(([command, args]) => {
    const message = BwsLlNbEncoder.encoder({ command, args });
    if (!message) return;
    response[command as Namespace.ConfigKeys] = message;
  });
  return response;
};

// Leituras da "Lista de comandos" do manual — usadas tanto na checagem quanto na
// validação pós-configuração.
const READ_MESSAGES = [
  { command: 'RIAP\r\n', key: 'r_apn', delay_before: 1000 },
  { command: 'RIP1\r\n', key: 'r_ip_primary' },
  { command: 'RIP2\r\n', key: 'r_ip_secondary' },
  { command: 'RID1\r\n', key: 'r_dns_primary' },
  { command: 'RID2\r\n', key: 'r_dns_secondary' },
  { command: 'RCM\r\n', key: 'r_data_transmission_moving' },
  { command: 'RCW\r\n', key: 'r_data_transmission_stopped' },
  { command: 'RCE\r\n', key: 'r_data_transmission_sos' },
  { command: 'RCTE\r\n', key: 'r_data_transmission_stock' },
  { command: 'RCD\r\n', key: 'r_data_transmission_disabled' },
  { command: 'RFGO\r\n', key: 'r_gps_every_cycle' },
  { command: 'RCG\r\n', key: 'r_gps_fix_timeout' },
  { command: 'RFOM\r\n', key: 'r_work_mode' },
  { command: 'RFST\r\n', key: 'r_sos_timeout' },
  { command: 'RFPO\r\n', key: 'r_power_string' },
  { command: 'RBTR\r\n', key: 'r_radio_behavior' },
] as const;

type ReadKey = (typeof READ_MESSAGES)[number]['key'];

// Converte as respostas lidas no mesmo formato do perfil (general + specific), para que a
// checagem de configuração compare chave a chave com o perfil desejado.
const parseConfig = (response: Partial<Record<ReadKey, string>>, model: BwsLlNbModel) => ({
  general: {
    apn: BwsLlNbParser.apn(response.r_apn ?? ''),
    ip_primary: BwsLlNbParser.ip_primary(response.r_ip_primary ?? ''),
    ip_secondary: BwsLlNbParser.ip_secondary(response.r_ip_secondary ?? ''),
    dns_primary: BwsLlNbParser.dns_primary(response.r_dns_primary ?? ''),
    dns_secondary: BwsLlNbParser.dns_secondary(response.r_dns_secondary ?? ''),
  },
  specific: {
    technology_system_name: model,
    data_transmission_moving: BwsLlNbParser.data_transmission_moving(
      response.r_data_transmission_moving ?? '',
    ),
    data_transmission_stopped: BwsLlNbParser.data_transmission_stopped(
      response.r_data_transmission_stopped ?? '',
    ),
    data_transmission_sos: BwsLlNbParser.data_transmission_sos(
      response.r_data_transmission_sos ?? '',
    ),
    data_transmission_stock: BwsLlNbParser.data_transmission_stock(
      response.r_data_transmission_stock ?? '',
    ),
    data_transmission_disabled: BwsLlNbParser.data_transmission_disabled(
      response.r_data_transmission_disabled ?? '',
    ),
    gps_every_cycle: BwsLlNbParser.gps_every_cycle(response.r_gps_every_cycle ?? ''),
    gps_fix_timeout: BwsLlNbParser.gps_fix_timeout(response.r_gps_fix_timeout ?? ''),
    work_mode: BwsLlNbParser.work_mode(response.r_work_mode ?? ''),
    sos_timeout: BwsLlNbParser.sos_timeout(response.r_sos_timeout ?? ''),
    power_string: BwsLlNbParser.power_string(response.r_power_string ?? ''),
    radio_behavior: BwsLlNbParser.radio_behavior(response.r_radio_behavior ?? ''),
  },
});

export const useBwsLlNb = (model: BwsLlNbModel = 'DM_BWS_LL_NB') => {
  const { ports, openPort, closePort, getReader, writeToPort, requestPort } = useSerialPort({});

  const { sendMultipleMessages, send } = useCommunication<ISerialPort>({
    openTransport: async (transport) => {
      await openPort(
        transport,
        {
          baudRate: 115200,
          stopBits: 2,
          bufferSize: 1024,
        },
        150,
      );
    },
    closeTransport: closePort,
    sendMessage: async (port, msg: Message<string, { check?: string; delay_before?: number }>) => {
      const reader = await getReader(port);
      if (!reader) throw new Error('Reader não disponível');
      const { command, timeout, delay_before, check } = msg;
      if (delay_before) await sleep(delay_before);
      console.info('-------------------------');
      console.info('[MESSAGE SENT]', command);
      await writeToPort(port, command);
      const response = await readResponse(reader, command, timeout, check);
      console.info('[RESPONSE MATCHED]', response);

      await reader.cancel();
      reader.releaseLock();
      return response;
    },
    sendMessageV2: async (
      port,
      msg: MessageV2<{
        delay_before?: number;
        delay_after?: number;
        check?: string;
      }>,
    ) => {
      const { command, delay_before, delay_after } = msg;
      if (delay_before) await sleep(delay_before);
      await writeToPort(port, command);
      console.log('[COMMAND]', command);
      if (delay_after) await sleep(delay_after);
    },
    options: {
      delayBetweenMessages: 300,
      maxRetriesPerMessage: 3,
      maxOverallRetries: 2,
    },
  });

  const handleDetection = useCallback(
    async (ports: ISerialPort[]) => {
      // Detecção enxuta (igual ao NB2 + LoRa): apenas reconhecimento do modem. O LRKEYS NÃO
      // é enviado aqui — ele despeja ~76 linhas no buffer e, como a detecção roda em loop a
      // cada 5s, o resíduo contaminava as leituras seguintes. As chaves LoRa são lidas só na
      // identificação.
      const messages = [
        { command: 'RIMEI\r\n' },
        { command: 'ICCID\r\n' },
        { command: 'RINS\r\n' },
        { command: 'RFW\r\n' },
      ] as const;
      return await Promise.all(
        ports.map(async (port) => {
          try {
            const { pos } = await send({
              transport: port,
              messages,
              preChunk: async (transport) => {
                await getReader(transport);
              },
              posChunk: async (transport) => {
                const reader = await getReader(transport);
                return await readReader(reader, 10000);
              },
            });

            const lines = pos ?? [];
            const pick = <T>(fn: (line: string) => T | undefined): T | undefined =>
              lines.map((line) => fn(line)).find((value) => value !== undefined);

            return {
              port,
              response: {
                imei: pick(BwsLlNbParser.imei),
                iccid: pick(BwsLlNbParser.iccid),
                serial: pick(BwsLlNbParser.serial),
                firmware: pick(BwsLlNbParser.firmware),
              },
            };
          } catch (error) {
            if (
              (error as Error).name === 'NetworkError' ||
              (error as Error).message?.includes('Failed to open serial port')
            ) {
              return { port };
            }
            console.error(`[ERROR] handleDetection use-bws-ll-nb (${model})`, error);
            return { port };
          }
        }),
      );
    },
    [send, getReader, model],
  );

  const handleGetConfig = useCallback(
    async (detected: Namespace.Detected[]) => {
      return await Promise.all(
        detected.map(async ({ port, equipment }) => {
          try {
            const { pos } = await send({
              transport: port,
              messages: READ_MESSAGES,
              preChunk: async (transport) => {
                await getReader(transport);
              },
              posChunk: async (transport) => {
                const reader = await getReader(transport);
                return await readReader(reader, 30000);
              },
            });

            const lines = pos ?? [];
            const response = Object.fromEntries(
              READ_MESSAGES.map(({ key, command }) => [key, matchResponse(lines, command)]),
            ) as Partial<Record<ReadKey, string>>;

            return {
              port,
              equipment,
              config: parseConfig(response, model),
              messages: READ_MESSAGES.map(({ key, command }) => ({
                key,
                request: command,
                response: response[key],
              })),
            };
          } catch (error) {
            console.error(`[ERROR] handleGetConfig use-bws-ll-nb (${model})`, error);
            return { port, equipment, messages: [], config: {} };
          }
        }),
      );
    },
    [send, getReader, model],
  );

  const handleConfiguration = useCallback(
    async (detected: Namespace.Detected[], configuration_profile: IConfigurationProfile) => {
      // Envia todas as escritas + leituras em bloco e depois lê tudo de uma vez, casando cada
      // resposta pela linha que contém o prefixo do comando (mesmo fluxo do NB2 + LoRa).
      const write_messages = typedObjectEntries(generateMessages(configuration_profile)).map(
        ([key, command]) => ({
          key: `w_${key}`,
          command,
          delay_after: 200,
        }),
      );

      const all_messages = [...write_messages, ...READ_MESSAGES];

      return await Promise.all(
        detected.map(async ({ port, equipment }) => {
          try {
            const init_time = Date.now();
            const { pos } = await send({
              transport: port,
              messages: all_messages,
              delayBetweenMessages: 200,
              preChunk: async (transport) => {
                await getReader(transport);
              },
              posChunk: async (transport) => {
                const reader = await getReader(transport);
                return await readReader(reader, 30000);
              },
            });
            const end_time = Date.now();

            const lines = pos ?? [];
            const response = Object.fromEntries(
              all_messages.map(({ key, command }) => [key, matchResponse(lines, command)]),
            ) as Record<string, string | undefined>;

            // status: todas as leituras precisam ter resposta.
            const status = READ_MESSAGES.every(({ key }) => typeof response[key] !== 'undefined');

            return {
              equipment,
              port,
              init_time,
              end_time,
              status,
              messages: all_messages.map(({ key, command }) => ({
                key,
                request: command,
                response: response[key],
              })),
              applied_profile: parseConfig(response as Partial<Record<ReadKey, string>>, model),
            };
          } catch (error) {
            console.error(`[ERROR] handleConfiguration use-bws-ll-nb (${model})`, error);
            return {
              port,
              status: false,
              equipment,
              messages: [],
              init_time: 0,
              end_time: 0,
            };
          }
        }),
      );
    },
    [send, getReader, model],
  );

  const handleAutoTest = useCallback(
    async (ports: ISerialPort[]) => {
      return await Promise.all(
        ports.map(async (port) => {
          const resultTemplate = {
            port,
            response: {} as Record<string, BwsLlNb.AutoTest | string | undefined>,
            messages: [
              { key: 'start', command: 'START\r\n' },
              { key: 'autotest_1', command: 'AUTOTEST' },
              { key: 'autotest_2', command: 'AUTOTEST' },
              { key: 'autotest_3', command: 'AUTOTEST' },
              { key: 'autotest_4', command: 'AUTOTEST' },
              { key: 'autotest_5', command: 'AUTOTEST' },
            ],
            analysis: {} as Record<string, boolean>,
            init_time: Date.now(),
            end_time: 0,
            status: false,
          };

          try {
            const startResponse = await sendMultipleMessages({
              transport: port,
              messages: [{ key: 'start', command: 'START\r\n' }] as const,
            });
            resultTemplate.response.start = startResponse.start;

            const autotestTimeout = 25000;
            const startTime = Date.now();
            let remainingAttempts = 5;
            let lastBattResult = false;

            while (remainingAttempts > 0 && Date.now() - startTime < autotestTimeout) {
              const key = `autotest_${5 - remainingAttempts + 1}`;
              try {
                await sleep(2000);

                const autotestResponse = await sendMultipleMessages({
                  transport: port,
                  messages: [
                    {
                      key,
                      command: 'AUTOTEST',
                      check: 'AUTOTEST=',
                      timeout: 4000,
                      transform: BwsLlNbParser.auto_test,
                    },
                  ] as const,
                });

                const autotest = autotestResponse[key];

                if (!autotest) continue;

                const BATT_VOLT = Number(autotest.BATT_VOLT);
                const TEMP = Number(autotest.TEMP);

                const isBattGood = !Number.isNaN(BATT_VOLT) && BATT_VOLT <= 43 && BATT_VOLT >= 40;

                // Sem entradas/saídas nem alimentação externa (uso sem alimentação do veículo):
                // IN1/IN2/OUT/VCC do NB2 + LoRa não se aplicam ao BWS LL NB. O RF (rádio
                // LoRa) só é cobrado nos modelos "+LoRa".
                resultTemplate.analysis = {
                  DEV: autotest.DEV === model,
                  ACELC: Boolean(autotest.ACELC?.length),
                  ACELP: autotest.ACELP === 'OK',
                  BATT_VOLT: lastBattResult || isBattGood,
                  CHARGER: autotest.CHARGER === 'OK',
                  FW: Boolean(autotest.FW?.length),
                  GPS: autotest.GPS === 'OK',
                  IC: isIccid(autotest.IC ?? ''),
                  ID_ACEL: Boolean(autotest.ID_ACEL?.length),
                  ID_MEM: Boolean(autotest.ID_MEM?.length),
                  IM: isImei(autotest.IM ?? ''),
                  MDM: autotest.MDM === 'OK',
                  RSSI: autotest.RSSI === 'OK',
                  SN: Boolean(autotest.SN?.length),
                  TEMP: !Number.isNaN(TEMP) && TEMP <= 28 && TEMP >= 23,
                  ...(hasLoraRadio(model) ? { RF: autotest.RF === 'OK' } : {}),
                };

                lastBattResult = resultTemplate.analysis.BATT_VOLT;

                const statusValues = Object.values(resultTemplate.analysis);
                resultTemplate.status = statusValues.length > 0 && statusValues.every(Boolean);

                resultTemplate.response[key] = autotest;

                if (resultTemplate.status) break;
              } catch (error) {
                console.warn(`Attempt ${5 - remainingAttempts + 1} failed`, error);
              }
              remainingAttempts--;
            }

            resultTemplate.end_time = Date.now();
            return resultTemplate;
          } catch (error) {
            console.error(`[ERROR] handleAutoTest use-bws-ll-nb (${model})`, error);
            resultTemplate.end_time = Date.now();
            return resultTemplate;
          }
        }),
      );
    },
    [sendMultipleMessages, model],
  );

  /**
   * @param id serial a ser gravado (extraído da etiqueta pelo operador)
   * @param imeiToWrite IMEI do cadastro (engineer.serial-relations), gravado antes do serial
   *                    como no NB2. Ausente quando o serial não tem relação cadastrada.
   */
  const handleIdentification = useCallback(
    async (detected: Namespace.Detected, id: string, imeiToWrite?: string) => {
      const { port, equipment } = detected;
      const hasLora = hasLoraRadio(model);

      // Timestamp epoch Unix em segundos, em HEX (8 caracteres), gravado no rádio junto
      // com o serial: o firmware deriva as chaves LoRaWAN dos dois.
      const timestamp_to_send = lorawanTimestamp();

      try {
        // Mesmo processo do NB2 + LoRa: grava o IMEI do cadastro (quando houver) e o SN no
        // modem e, nos modelos "+LoRa", também o SN e o timestamp no rádio. Depois vêm as
        // leituras de confirmação e o LRKEYS por ÚLTIMO, para o dump das chaves (~76
        // linhas) não contaminar as respostas de RINS/LRINS/LRTK.
        const messages = [
          ...(imeiToWrite ? [{ command: `WIMEI=${imeiToWrite}\r\n`, delay_after: 1000 }] : []),
          { command: `WINS=${id}\r\n`, delay_after: 1000 },
          ...(hasLora
            ? [
                { command: `LWINS=${id}\r\n`, delay_after: 1000 },
                { command: `LWTK=${timestamp_to_send}\r\n`, delay_after: 1000 },
              ]
            : []),
          // Pausa extra antes das leituras para o dispositivo terminar de persistir os
          // valores gravados.
          { command: 'RINS\r\n', delay_before: 2000, delay_after: 500 },
          { command: 'RIMEI\r\n', delay_after: 500 },
          ...(hasLora
            ? [
                { command: 'LRINS\r\n', delay_after: 500 },
                { command: 'LRTK\r\n', delay_after: 500 },
                { command: 'LRKEYS\r\n' },
              ]
            : []),
        ];

        const hasAllKeys = (source: string[]) =>
          [
            BwsLlNbParser.device_address,
            BwsLlNbParser.device_eui,
            BwsLlNbParser.application_eui,
            BwsLlNbParser.application_key,
            BwsLlNbParser.application_session_key,
            BwsLlNbParser.network_session_key,
          ].every((parser) => source.some((line) => parser(line) !== undefined));

        const hasSosTimeoutAck = (source: string[]) =>
          source.some((line) => BwsLlNbParser.sos_timeout_ack(line) !== undefined);

        // A leitura roda EM PARALELO ao envio (igual ao NB2 + LoRa): lendo só depois de
        // todos os comandos, o buffer da porta (1024 bytes) enchia e o dump do LRKEYS,
        // que chega por último, era descartado.
        const sendAndCollect = async (
          toSend: { command: string; delay_before?: number; delay_after?: number }[],
          isComplete: (source: string[]) => boolean,
        ) => {
          let collector: ReturnType<typeof collectLines> | undefined;
          const { pos } = await send({
            transport: port,
            messages: toSend,
            delayBetweenMessages: 200,
            preChunk: async (transport) => {
              collector = collectLines(await getReader(transport));
            },
            posChunk: async () => {
              if (!collector) return [];
              // Encerra quando o dispositivo silencia: 500ms se a resposta esperada já
              // chegou, 4s caso contrário (o dump do LRKEYS pode demorar a começar).
              const start = Date.now();
              while (Date.now() - start < 30000) {
                const idleLimit = isComplete(collector.snapshot()) ? 500 : 4000;
                if (Date.now() - collector.lastDataAt() > idleLimit) break;
                await sleep(100);
              }
              return collector.snapshot();
            },
          });
          return pos ?? [];
        };

        const init_time = Date.now();
        const lines = await sendAndCollect(messages, (source) => !hasLora || hasAllKeys(source));

        // Retry do LRKEYS (até 2x) quando as chaves não vieram na primeira leitura.
        for (let retry = 0; hasLora && retry < 2 && !hasAllKeys(lines); retry++) {
          console.info(`[${model} RETRY #${retry + 1}] LRKEYS: chaves não lidas, reenviando`);
          lines.push(...(await sendAndCollect([{ command: 'LRKEYS\r\n' }], hasAllKeys)));
        }

        // Ao final de todo o processo, grava o time out de SOS de fábrica. O dispositivo
        // confirma ecoando o comando ("WFST=10").
        lines.push(
          ...(await sendAndCollect(
            [{ command: `WFST=${IDENTIFICATION_SOS_TIMEOUT_MIN}\r\n` }],
            hasSosTimeoutAck,
          )),
        );
        const end_time = Date.now();

        const pick = <T>(fn: (line: string) => T | undefined): T | undefined =>
          lines.map((line) => fn(line)).find((value) => value !== undefined);

        // NB e rádio respondem o serial com o mesmo prefixo "RINS=" (o LRINS volta sem
        // o "L"), então o parser separa as duas respostas pela ordem de chegada. O
        // firmware devolve o serial sem os zeros à esquerda (grava 02010A16, responde
        // 2010A16), então completamos até o tamanho do serial gravado antes de comparar.
        const padSerial = (serial: string | undefined) =>
          serial ? serial.toUpperCase().padStart(id.length, '0') : undefined;
        const serials = BwsLlNbParser.identification_serials(lines);
        const nb_serial = padSerial(serials.nb);
        const lora_serial = padSerial(serials.lora);
        const nb_imei = pick(BwsLlNbParser.imei);
        const timestamp = pick(BwsLlNbParser.timestamp)?.toUpperCase();
        const sos_timeout = pick(BwsLlNbParser.sos_timeout_ack);

        // As chaves lidas precisam bater com as geradas pela fórmula do firmware a partir
        // do serial e do timestamp gravados.
        const keys = verifyLorawanKeys(id, timestamp_to_send, {
          device_address: pick(BwsLlNbParser.device_address),
          device_eui: pick(BwsLlNbParser.device_eui),
          application_eui: pick(BwsLlNbParser.application_eui),
          application_key: pick(BwsLlNbParser.application_key),
          application_session_key: pick(BwsLlNbParser.application_session_key),
          network_session_key: pick(BwsLlNbParser.network_session_key),
        });

        // Com IMEI do cadastro, a leitura (RIMEI) precisa confirmar a gravação — senão o banco
        // registraria um IMEI que o dispositivo não tem.
        const matchImei = !imeiToWrite || nb_imei === imeiToWrite;
        const matchLora =
          !hasLora || (id === lora_serial && timestamp_to_send === timestamp && keys.valid);
        const matchSosTimeout = sos_timeout === IDENTIFICATION_SOS_TIMEOUT_MIN;

        const status = id === nb_serial && matchImei && matchLora && matchSosTimeout;

        // Na reprovação, relata cada conferência (esperado x recebido) e as linhas cruas
        // recebidas, para diagnosticar a falha sem precisar reproduzir.
        if (!status) {
          type Check = [name: string, expected: string, received: string | undefined];
          const checks: Check[] = [
            ['serial NB (RINS)', id, nb_serial],
            ...(imeiToWrite ? [['IMEI (RIMEI)', imeiToWrite, nb_imei] as Check] : []),
            ...(hasLora
              ? [
                  ['serial LoRa (LRINS)', id, lora_serial] as Check,
                  ['timestamp (LRTK)', timestamp_to_send, timestamp] as Check,
                  ...typedObjectEntries(keys.expected).map(
                    ([key, expected]): Check => [key, expected, keys.read[key]],
                  ),
                ]
              : []),
            [
              'time out de SOS (WFST)',
              String(IDENTIFICATION_SOS_TIMEOUT_MIN),
              sos_timeout === undefined ? undefined : String(sos_timeout),
            ],
          ];
          console.error(
            [
              `[${model} IDENTIFICATION FAILED]`,
              ...checks.map(
                ([name, expected, received]) =>
                  `  ${expected === received ? 'ok    ' : 'FALHOU'} ${name}: esperado ${expected}, recebido ${received}`,
              ),
              `  linhas recebidas (${lines.length}):`,
              ...lines.map((line, i) => `  ${i}: ${JSON.stringify(line)}`),
            ].join('\n'),
          );
        }

        return {
          port,
          init_time,
          end_time,
          status,
          messages: [
            ...(imeiToWrite ? [{ key: 'write_nb_imei', request: `WIMEI=${imeiToWrite}\r\n` }] : []),
            { key: 'write_nb_serial', request: `WINS=${id}\r\n` },
            ...(hasLora
              ? [
                  { key: 'write_lora_serial', request: `LWINS=${id}\r\n` },
                  { key: 'write_lora_timestamp', request: `LWTK=${timestamp_to_send}\r\n` },
                ]
              : []),
            { key: 'read_nb_serial', request: 'RINS\r\n', response: nb_serial },
            { key: 'read_nb_imei', request: 'RIMEI\r\n', response: nb_imei },
            ...(hasLora
              ? [
                  { key: 'read_lora_serial', request: 'LRINS\r\n', response: lora_serial },
                  { key: 'read_timestamp', request: 'LRTK\r\n', response: timestamp },
                  {
                    key: 'read_lora_keys',
                    request: 'LRKEYS\r\n',
                    // O log de identificação só aceita string em `response`.
                    response: keys.complete ? JSON.stringify(keys.read) : undefined,
                  },
                  ...(keys.complete
                    ? [
                        {
                          key: 'check_lora_keys',
                          request: 'CHECK',
                          response: keys.valid
                            ? '✓ Chaves OK'
                            : `✗ Chaves divergentes: ${keys.mismatched.join(', ')}`,
                        },
                      ]
                    : []),
                ]
              : []),
            {
              key: 'write_sos_timeout',
              request: `WFST=${IDENTIFICATION_SOS_TIMEOUT_MIN}\r\n`,
              response: sos_timeout === undefined ? undefined : `WFST=${sos_timeout}`,
            },
          ],
          equipment_before: {
            // Dispositivo virgem ainda não tem serial; o log exige string.
            serial: equipment.serial ?? '',
            imei: equipment.imei,
            lora_keys: equipment.lora_keys,
          },
          equipment_after: {
            serial: nb_serial,
            imei: nb_imei,
            // Sem rádio não há chaves: o cadastro na KORA é só dos modelos "+LoRa".
            ...(hasLora ? { lora_keys: { timestamp, ...keys.read } } : {}),
          },
        };
      } catch (error) {
        const message = `[(${new Date().toLocaleString()}) ERROR IN ${model} IDENTIFICATION]`;
        console.error(message, error);
        throw new Error(message);
      }
    },
    [send, getReader, model],
  );

  const handleFirmwareUpdate = useCallback(
    async (detected: Namespace.Detected[]) => {
      return await Promise.all(
        detected.map(async ({ port, equipment }) => {
          try {
            const messages = [{ key: 'update_firmware', command: `UPFW\r\n` }] as const;

            const init_time = Date.now();
            const response = await sendMultipleMessages({
              transport: port,
              messages,
            });
            const status = response.update_firmware && response.update_firmware.length > 0;

            const end_time = Date.now();
            return {
              port,
              init_time,
              end_time,
              status,
              messages: messages.map(({ key, command }) => ({
                key,
                request: command,
                response: response[key],
              })),
              equipment: {
                serial: equipment.serial,
                imei: equipment.imei,
                firmware: equipment.firmware,
                lora_keys: equipment.lora_keys,
              },
            };
          } catch (error) {
            const message = `[(${new Date().toLocaleString()}) ERROR IN ${model} FIRMWARE UPDATE]`;
            console.error(message, error);
            throw new Error(message);
          }
        }),
      );
    },
    [sendMultipleMessages, model],
  );

  const isIdentified = (input?: {
    imei?: string;
    iccid?: string;
    serial?: string;
    firmware?: string;
  }): 'fully_identified' | 'partially_identified' | 'not_identified' => {
    if (!input) return 'not_identified';
    const { serial, imei, iccid, firmware } = input;
    const identified = [serial, imei, iccid, firmware];
    if (identified.every((e) => e && e.length > 0)) {
      return 'fully_identified';
    } else if (identified.some((e) => e && e.length > 0)) {
      return 'partially_identified';
    } else {
      return 'not_identified';
    }
  };

  return {
    isIdentified,
    ports,
    handleIdentification,
    handleGetConfig,
    handleConfiguration,
    requestPort,
    handleAutoTest,
    handleDetection,
    handleFirmwareUpdate,
  };
};

export const useBwsLlNbLora = () => useBwsLlNb('DM_BWS_LL_NB_LORA');

export const useBwsBaitNb = () => useBwsLlNb('DM_BWS_BAIT_NB');

export const useBwsBaitNbLora = () => useBwsLlNb('DM_BWS_BAIT_NB_LORA');
